import { useState, useEffect, useCallback } from 'react';

import { BoolNode } from 'src/services/common.types';
import { IOrgCandidateListItem } from 'src/services/candidates/candidates.types';
import {
  CandidateSearchEvent,
  streamCandidateSearch,
  CandidateKeywordQuery,
  streamCandidateKeywordSearch,
} from 'src/services/candidates/candidate-search-stream';

export type CandidateSearchStatus = 'idle' | 'queued' | 'searching' | 'done' | 'error';

export interface CandidateSearchStreamState {
  rows: IOrgCandidateListItem[];
  status: CandidateSearchStatus;
  /** Candidates checked so far, and how many there are, for the progress bar. */
  checked: number;
  total: number;
  /** Stopped at the match cap with candidates still unchecked: "1,000+". */
  capped: boolean;
  /** Finished, but some candidates could not be checked. */
  partial: boolean;
}

export interface CandidateSearchStream extends CandidateSearchStreamState {
  /** Patches one loaded row in place, for edits made from the list such as a rating. */
  updateRow: (id: string, patch: Partial<IOrgCandidateListItem>) => void;
  /** Drops one loaded row, for a candidate removed from the list. */
  removeRow: (id: string) => void;
}

const INITIAL: CandidateSearchStreamState = { rows: [], status: 'idle', checked: 0, total: 0, capped: false, partial: false };

type Start = (signal: AbortSignal, onEvent: (event: CandidateSearchEvent) => void) => Promise<void>;

/**
 * Runs a streamed candidate search (JT1-1802) and accumulates its results as they arrive.
 *
 * A change of `identity` or `key`, or `enabled` turning true, starts a fresh search, and the previous one is
 * aborted, so the server stops querying for it and none of its late rows can leak into the new
 * results. Unmounting aborts too. `identity` compares by reference (a new DSL object is a new
 * search, so pressing Search again re-runs it); `key` by value. `start` is read when either
 * changes, so it may be a new function on every render.
 */
function useStreamedSearch(start: Start, identity: unknown, key: string, enabled: boolean): CandidateSearchStream {
  const [state, setState] = useState<CandidateSearchStreamState>(INITIAL);

  useEffect(() => {
    if (!enabled) {
      setState(INITIAL);
      return undefined;
    }

    const controller = new AbortController();
    setState({ ...INITIAL, status: 'searching' });

    const apply = (event: CandidateSearchEvent) => {
      if (controller.signal.aborted) return;
      setState((prev) => {
        switch (event.type) {
          case 'queued':
            return { ...prev, status: 'queued' };
          case 'rows': {
            const seen = new Set(prev.rows.map((row) => row.id));
            return { ...prev, status: 'searching', rows: [...prev.rows, ...event.items.filter((row) => !seen.has(row.id))] };
          }
          case 'progress':
            return { ...prev, status: 'searching', checked: event.checked, total: event.total };
          case 'done':
            return { ...prev, status: 'done', capped: event.capped, partial: !!event.partial, checked: prev.total || prev.checked };
          case 'error':
            // rows already found stay on screen; the error says the list may be incomplete
            return { ...prev, status: 'error' };
          default:
            return prev;
        }
      });
    };

    start(controller.signal, apply).catch(() => {
      // fetch rejects when aborted, which is expected; anything else is a failed search
      if (!controller.signal.aborted) apply({ type: 'error' });
    });

    return () => controller.abort();
    // `start` is deliberately not a dependency: `identity` and `key` say when the search changed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [identity, key, enabled]);

  const updateRow = useCallback((id: string, patch: Partial<IOrgCandidateListItem>) => {
    setState((prev) => ({ ...prev, rows: prev.rows.map((row) => (row.id === id ? { ...row, ...patch } : row)) }));
  }, []);

  const removeRow = useCallback((id: string) => {
    setState((prev) => ({ ...prev, rows: prev.rows.filter((row) => row.id !== id) }));
  }, []);

  return { ...state, updateRow, removeRow };
}

/** The advanced search (JT1-1803): a new `dsl` object or new column filters start a new search. */
export function useCandidateSearchStream({
  dsl,
  columnFilters,
  enabled,
}: {
  dsl: BoolNode;
  columnFilters?: Record<string, string>;
  enabled: boolean;
}): CandidateSearchStream {
  return useStreamedSearch(
    (signal, onEvent) => streamCandidateSearch({ dsl, columnFilters, signal }, onEvent),
    dsl,
    JSON.stringify(columnFilters ?? {}),
    enabled,
  );
}

/** The keyword box (JT1-1805): any change to the query or its filters starts a new search. */
export function useCandidateKeywordStream({
  query,
  enabled,
}: {
  query: CandidateKeywordQuery;
  enabled: boolean;
}): CandidateSearchStream {
  return useStreamedSearch(
    (signal, onEvent) => streamCandidateKeywordSearch({ query, signal }, onEvent),
    null,
    JSON.stringify(query),
    enabled,
  );
}
